import { WhereItIs, fullLabel } from "../components/WhereItIs";
import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { prov } from "@/lib/provenance";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { scanAccept, scanRefuse } from "@/lib/scanFeedback";
import { SoundToggle } from "@/components/SoundToggle";
import { ScanField } from "@/components/ScanField";
import { ScanLine, CheckCircle2, XCircle, MapPin, Volume2, VolumeX, RotateCcw, Undo2 } from "lucide-react";

const money = (n) => (n == null ? "" : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

export default function ReturnsPage() {
  const [locations, setLocations] = useState([]);
  const [binValue, setBinValue] = useState("");
  const [binLabel, setBinLabel] = useState("");
  const [log, setLog] = useState([]);
  const [last, setLast] = useState(null);

  useEffect(() => {
    api.get("/locations").then(({ data }) => setLocations(data.filter((l) => l.type !== "site"))).catch(() => {});
  }, []);

  const pickBin = (id) => {
    const l = locations.find((x) => x.id === id);
    setBinValue(id);
    setBinLabel(l ? `${l.short_code} · ${fullLabel(l)}` : id);
  };

  // PRIMARY path — scan a bin OR a tote/pallet to activate the destination
  const activateByScan = async (code) => {
    if (!code) return;
    try {
      const { data } = await api.get("/locations/resolve", { params: { scan: code } });
      setBinValue(data.id);
      setBinLabel(`${data.short_code} · ${fullLabel(data)}`);
      scanAccept(`RETURNING TO ${data.short_code}`);
      toast.success(`Destination set → ${data.short_code}`);
    } catch (e) {
      scanRefuse(formatApiErrorDetail(e.response?.data?.detail) || "That scan is not a known bin or container.");
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "That scan is not a known bin or container.");
    }
  };

  const doReturn = async (code, meta) => {
    if (!code) return;
    if (!binValue) { toast.error("Set the destination bin first."); return; }
    try {
      const { data } = await api.post("/returns/scan", { part_scan: code, location_id: binValue,
                                                         provenance: prov("returns_scan", meta) });
      if (data.ok) {
        scanAccept(data.part?.short_code || "BACK ON THE SHELF");
        setLast({ ok: true, ...data });
        setLog((l) => [{ ok: true, code, part: data.part, reversal: data.reversal, ts: Date.now() }, ...l].slice(0, 100));
      } else {
        scanRefuse(data.message || "THAT CAN'T COME BACK");
        setLast({ ok: false, message: data.message });
        setLog((l) => [{ ok: false, code, message: data.message, ts: Date.now() }, ...l].slice(0, 100));
      }
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail);
      scanRefuse(msg || "THAT CAN'T COME BACK");
      setLast({ ok: false, message: msg });
      setLog((l) => [{ ok: false, code, message: msg, ts: Date.now() }, ...l].slice(0, 100));
    }
  };

  const okCount = log.filter((x) => x.ok).length;
  const errCount = log.length - okCount;

  return (
    <div data-testid="returns-page">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight flex items-center gap-2"><RotateCcw className="w-6 h-6 text-[#B01E28]" />Returns to Stock</h1>
          <p className="text-sm text-slate-500">Scan a pulled or sold part back into a bin. It goes Available again and its recovery credit is automatically reversed.</p>
        </div>
        <SoundToggle />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-5">
        <div className="space-y-4">
          {/* Destination — scan a bin OR tote/pallet to activate (PRIMARY); dropdown kept as FALLBACK */}
          <div className="border border-slate-200 bg-white rounded-xl shadow-sm p-4">
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1 mb-2"><MapPin className="w-3.5 h-3.5 text-[#B01E28]" />Destination bin or container</Label>
            <ScanField testid="dest-scan" submitLabel="Set"
              placeholder="Scan a bin or tote/pallet to set the destination"
              onScan={activateByScan} />
            {binValue && <div data-testid="active-bin" className="mt-2 text-sm font-mono bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-1.5">ACTIVE → {binLabel}</div>}
            <details className="mt-3" data-testid="dest-fallback">
              <summary className="text-xs font-mono text-slate-500 cursor-pointer hover:text-slate-700">…or pick manually (fallback)</summary>
              <div className="flex flex-col sm:flex-row gap-2 mt-2">
                <Select value={binValue} onValueChange={pickBin}>
                  <SelectTrigger data-testid="bin-select" className="rounded-lg border-slate-200"><SelectValue placeholder="Pick a bin / container" /></SelectTrigger>
                  <SelectContent className="rounded-lg max-h-72">{locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.short_code} · {fullLabel(l)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </details>
          </div>

          {/* Scan field — shared widget: camera + scan-gun + manual + QR-URL normalize */}
          <div className="border border-slate-200 bg-white rounded-xl shadow-sm p-4">
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1 mb-2"><ScanLine className="w-3.5 h-3.5 text-[#B01E28]" />Scan returning part</Label>
            {binValue ? (
              <ScanField testid="part-scan" submitLabel="Return" continuous
                placeholder="Scan or type part QR / barcode / FAS-code"
                onScan={doReturn} />
            ) : (
              <p className="text-sm text-slate-400">Set the destination bin first to start scanning.</p>
            )}
          </div>

          {last && (
            <div data-testid="returns-flash" className={`border-2 rounded-xl p-6 text-center ${last.ok ? "border-emerald-500 bg-emerald-50" : "border-red-500 bg-red-50"}`}>
              {last.ok ? <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-600" /> : <XCircle className="w-12 h-12 mx-auto text-red-600" />}
              {last.ok ? (
                <div className="mt-2">
                  <div className="font-display text-2xl font-black">{last.part.short_code}</div>
                  <div className="text-sm text-slate-700">{last.part.name} · {last.part.brand}</div>
                  <div className="text-xs text-emerald-700 mt-1 flex items-center justify-center gap-1"><WhereItIs location={last.location} size="sm" testid="returns-where" /> · BACK IN STOCK</div>
                  {last.reversal && <div className="inline-flex items-center gap-1 mt-2 text-xs font-mono bg-amber-100 text-amber-800 border border-amber-200 rounded px-2 py-1"><Undo2 className="w-3 h-3" />Reversed {last.reversal.credit_type} credit {money(last.reversal.value)}{last.reversal.cart_number ? ` · ${last.reversal.cart_number}` : ""}</div>}
                </div>
              ) : (
                <div className="mt-2 font-mono text-sm text-red-700">{last.message}</div>
              )}
            </div>
          )}
        </div>

        <div className="border border-slate-200 bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="bg-slate-50 px-3 py-2 font-mono text-[10px] uppercase tracking-wider text-slate-500 flex justify-between border-b border-slate-100">
            <span>Session log</span>
            <span><span className="text-emerald-600" data-testid="ok-count">{okCount} ok</span> · <span className="text-red-600" data-testid="err-count">{errCount} err</span></span>
          </div>
          <div className="max-h-[60dvh] overflow-y-auto">
            {log.length === 0 ? <div className="p-6 text-center font-mono text-xs text-slate-400">NO RETURNS YET</div>
              : log.map((x, i) => (
                <div key={i} className={`flex items-center gap-2 px-3 py-2 border-t border-slate-100 text-xs ${x.ok ? "" : "bg-red-50"}`}>
                  {x.ok ? <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" /> : <XCircle className="w-4 h-4 text-red-600 shrink-0" />}
                  {x.ok ? <span className="font-mono">{x.part.short_code} <span className="text-slate-500">{x.part.name}</span>{x.reversal ? <span className="text-amber-700"> · -{money(x.reversal.value)}</span> : ""}</span>
                    : <span className="font-mono text-red-700 truncate">{x.code}: {x.message}</span>}
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
}
